import { useMemo, useState } from 'react';
import useSWR from 'swr';
import { Link } from 'react-router-dom';
import {
 Table,
 Thead,
 Tbody,
 Tr,
 Th,
 Td,
 Input,
 InputGroup,
 InputLeftElement,
 Button,
 Text,
 Box,
 Flex,
} from '@chakra-ui/react';
import { CiSearch } from 'react-icons/ci';
import { FiGlobe } from 'react-icons/fi';
import baseService from '../../services/base';
import Loader from '../../components/Feedback/Loader';
import { MainContent, PageHeaderStructure } from '../../components/Layout';
import { Select } from '../../components/Input';

async function fetchGroups(url) {
 const response = await baseService.get(url);
 return response?.data || [];
}

const PLAN_FILTER_OPTIONS = [
 { value: '', label: 'Todos os planos' },
 { value: 'paid', label: 'Cliente pago' },
 { value: 'freemium', label: 'Freemium' },
];

const SORT_OPTIONS = [
 { value: 'name', label: 'Nome (A–Z)' },
 { value: 'recent', label: 'Mais recentes' },
];

// EG-70 (Fase 2) — lista de empresas/grupos com espaço de documentação. Todo
// company_group já tem espaço (não existe um "habilitar" separado); esta
// tela é só o ponto de entrada pra escolher qual abrir.
export default function Documents() {
 const { data, isLoading } = useSWR('/document/company-groups', fetchGroups);
 const [search, setSearch] = useState('');
 const [planFilter, setPlanFilter] = useState('');
 const [sortBy, setSortBy] = useState('name');

 const filtered = useMemo(() => {
  let rows = [...(data || [])];
  if (search.trim()) {
   const term = search.trim().toLowerCase();
   rows = rows.filter(
    (row) =>
     row.company_name?.toLowerCase().includes(term) ||
     row.company_fantasy_name?.toLowerCase().includes(term)
   );
  }
  if (planFilter === 'freemium') rows = rows.filter((row) => row.is_freemium);
  if (planFilter === 'paid') rows = rows.filter((row) => !row.is_freemium);
  // Backend já devolve por nome; "recentes" usa a data de cadastro do grupo.
  if (sortBy === 'recent') {
   rows.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
  }
  return rows;
 }, [data, search, planFilter, sortBy]);

 if (isLoading) return <Loader />;

 return (
  <MainContent>
   <PageHeaderStructure
    title='Documentação'
    subtitle='Pastas e arquivos por empresa'
   />

   {/* "Documentos Gerais" (Lucas, 21/09) — pasta singleton visível pra
       TODAS as empresas, fora da lista/filtro abaixo (não é um company_group). */}
   <Flex
    as={Link}
    to='/documents/global'
    align='center'
    gap='12px'
    bg='white'
    borderRadius='12px'
    border='1px solid #E7E9EA'
    p='16px'
    mb='24px'
    cursor='pointer'
    _hover={{ bg: '#F9FAFA' }}
   >
    <FiGlobe size={22} color='#2F80ED' />
    <Box>
     <Text fontSize='sm' fontWeight={700}>
      Documentos Gerais
     </Text>
     <Text fontSize='xs' color='#818A8F'>
      Visível pra todas as empresas
     </Text>
    </Box>
   </Flex>

   <Flex gap='12px' mb='24px' wrap='wrap'>
    <Box maxW='420px' flex='1' minW='240px'>
     <InputGroup>
      <InputLeftElement pointerEvents='none'>
       <CiSearch color='#818A8F' />
      </InputLeftElement>
      <Input
       placeholder='Buscar empresa por nome'
       bg='white'
       value={search}
       onChange={(e) => setSearch(e.target.value)}
      />
     </InputGroup>
    </Box>
    <Box maxW='220px' minW='180px'>
     <Select
      name='planFilter'
      bg='white'
      value={planFilter}
      onChange={(e) => setPlanFilter(e.target.value)}
      options={PLAN_FILTER_OPTIONS}
     />
    </Box>
    <Box maxW='220px' minW='180px'>
     <Select
      name='sortBy'
      bg='white'
      value={sortBy}
      onChange={(e) => setSortBy(e.target.value)}
      options={SORT_OPTIONS}
     />
    </Box>
   </Flex>

   <Box bg='white' borderRadius='12px' overflow='hidden'>
    <Table variant='simple'>
     <Thead>
      <Tr>
       <Th>Empresa</Th>
       <Th>Nome fantasia</Th>
       <Th w='160px'></Th>
      </Tr>
     </Thead>
     <Tbody>
      {filtered?.length === 0 && (
       <Tr>
        <Td colSpan={3}>
         <Text color='#818A8F' py='4'>
          Nenhuma empresa encontrada.
         </Text>
        </Td>
       </Tr>
      )}
      {filtered?.map((row) => (
       <Tr key={row.id_company_group}>
        <Td>{row.company_name || `Grupo ${row.id_company_group}`}</Td>
        <Td>{row.company_fantasy_name || '—'}</Td>
        <Td>
         <Button
          as={Link}
          to={`/documents/${row.id_company_group}`}
          variant='outline'
          size='sm'
         >
          Ver pastas
         </Button>
        </Td>
       </Tr>
      ))}
     </Tbody>
    </Table>
   </Box>
  </MainContent>
 );
}
